import React, { useState, useEffect, useRef, useContext } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { CardsGrid, Loader, TextInput, ThemeContext } from 'quantux-ui';
import { gray, blue } from '../../theme';
import { BASE_URL, DRAWER_BREAK_POINT, getHighlightedText } from '../../utils';

const DOCS_PER_PAGE = 50;

const TaxonomyDataGrid = () => {
  const [gridData, setGridData] = useState([]);
  const [gridWidth, setGridWidth] = useState(window.innerWidth <= 768 ? window.innerWidth - 16 : 550);
  const [gridHeight, setGridHeight] = useState(window.innerHeight - 143);
  const [showLoader, setShowLoader] = useState(false);
  const [docsCount, setDocsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isFiltered, setIsFiltered] = useState(false);

  const gridParentRef = useRef();

  const context = useContext(ThemeContext);
  const { isTouchDevice } = context.theme;

  let gcHeightDiff = '25px';
  if (isTouchDevice) {
    if (/iPhone/ig.test(window.navigator.userAgent)) {
      gcHeightDiff = '105px';
    } else {
      gcHeightDiff = '50px';
    }
  }

  const styles = StyleSheet.create({
    gridContainer: {
      height: `calc(100% - ${gcHeightDiff})`,
      overflowY: 'hidden',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    gridToolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '40px',
      // border: '1px solid black',
      // borderBottom: 'none',
      marginBottom: '2px',
      paddingLeft: '4px',
      background: gray[500],
      width: '100%'
    },
    gridToolbarIcon: {
      height: '50px',
      width: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      ':hover': {
        color: blue[500]
      }
    },
    gridCard: {
      background: gray[500],
      color: blue[200],
      paddingBottom: '4px'
    },
    bulletItem: {
      fontSize: '10px'
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '14px',
      padding: '4px',
      height: '48px',
      overflow: 'hidden',
      borderBottom: `1px solid ${gray[800]}`
    },
    cardDataContainer: {
      height: '180px',
      overflowY: 'auto',
      padding: '8px'
    }
  });

  const getData = async () => {
    setShowLoader(true);

    let isCode = false;
    const tmpFilters = {};
    if (search.length > 0) {
      if (/^\d/.test(search)) {
        isCode = true;
      }

      if (isCode) {
        tmpFilters.code = search;
      } else {
        tmpFilters.searchTerm = search;
      }
    }

    const formData = new FormData();
    formData.append('skip', (currentPage - 1) * DOCS_PER_PAGE);
    formData.append('limit', DOCS_PER_PAGE);
    formData.append('filters', JSON.stringify(tmpFilters));

    const reqHeaders = new Headers();
    // const token = localStorage.getItem('token');
    // reqHeaders.append('Authorization', `Bearer ${token}`);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    const dataPromise = fetch(`${BASE_URL}/taxonomy/all`, requestOptions);
    const countPromise = fetch(`${BASE_URL}/taxonomy/count`, requestOptions);

    const response = await Promise.all([dataPromise, countPromise])
      .catch(err => {
        console.log('Error getting taxonomy grid data and count');
        console.log(err);
      });

    if (response) {
      const dataResponse = await response[0].json();
      const countResponse = await response[1].json();

      if (dataResponse && dataResponse.success) {
        setIsFiltered(Object.keys(tmpFilters).length > 0);
        setGridData(dataResponse.taxonomyData);
        setShowLoader(false);
      }

      if (countResponse && countResponse.success) {
        console.log(`[Debug] Total taxonomy count = ${countResponse.taxonomyCount}`);
        setDocsCount(countResponse.taxonomyCount);
      }
    }
  };

  const handleWindowResize = () => {
    if (gridParentRef && gridParentRef.current) {
      const gridParentRefWidth = gridParentRef.current.getBoundingClientRect().width;
      console.log(`gridParentRef width = ${gridParentRefWidth}`);
      // const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : Math.ceil(0.75 * gridParentRefWidth);
      const tmp = window.innerWidth <= 768 ? window.innerWidth - 16 : gridParentRefWidth;
      console.log(`GridWidth = ${tmp}`);
      // setGridWidth(Math.min(tmp, 1111));
      setGridWidth(Math.min(tmp, 1111));
      // setGridHeight(window.innerHeight - 400);
      setGridHeight(window.innerHeight - 150);
      // setMobileLayout(window.innerWidth <= DRAWER_BREAK_POINT);
    }
  };

  // Get cards grid data
  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    getData();
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [currentPage]);

  useEffect(() => {
    if (search.length === 0) {
      getData();
    }
  }, [search]);

  const handleOnEditSearch = (e) => {
    const { value } = e.target;
    setIsFiltered(false);
    setSearch(value);
  };

  const handleOnClickSearch = () => {
    getData();
  };

  const handleOnClickResetFilters = () => {
    setSearch('');
  };

  const drawerWidth = window.innerWidth <= DRAWER_BREAK_POINT ? window.innerWidth : 320;
  let cardWidth = 600;
  if ((window.innerWidth - drawerWidth) < 600) {
    cardWidth = 326;
  }

  return (
    <div ref={gridParentRef} className={`p-2 ${css(styles.gridContainer)}`}>
      {showLoader &&
        <Loader
          iconType="cog"
          iconSize="5x"
          text="Loading Data"
          style={{
            position: 'absolute',
            background: 'black',
            opacity: '0.9'
          }}
        />
      }
      {/* Grid Toolbar */}
      <div className={css(styles.gridToolbar)}>
        {/* Grid toolbar - left aligned search */}
        <div className='fx' style={{ alignItems: 'center' }}>
          <TextInput
            value={search}
            placeholder='Search Taxonomy'
            onChange={handleOnEditSearch}
            style={{
              color: 'white',
              width: '359px',
              background: '#194877',
              borderBottom: `1px solid ${gray[200]}`
            }}
          />
          <div
            className='ml-2 fx fx__hv-center'
            style={{
              width: '32px',
              height: '32px',
              background: blue[400]
            }}
            onClick={handleOnClickSearch}
          >
            <i
              style={{ color: 'white' }} className='fa fa-search'
              onClick={handleOnClickSearch}
            />
          </div>
        </div>
        {/* Grid toolbar - right aligned buttons */}
        <div className='fx'>
          {/* Reset button */}
          <div
            className={css(styles.gridToolbarIcon)}
            onClick={handleOnClickResetFilters}
          >
            <i className="fa fa-lg fa-refresh" />
          </div>
          {/* Vertical ellipsis */}
          <div
            className={css(styles.gridToolbarIcon)}
            style={{ fontSize: '16px' }}
          >
            <span className='fa-stack'>
              <i className='fa fa-circle fa-stack-2x' />
              <i
                className='fa fa-ellipsis-v fa-stack-1x'
                style={{ color: gray[700] }}
              />
              {/* <i class="fa fa-flag fa-stack-1x fa-inverse"></i> */}
            </span>
          </div>
        </div>
      </div>
      <CardsGrid
        rowData={gridData}
        gridWidth={gridWidth}
        gridHeight={gridHeight}
        loader={false}
        style={{ flexGrow: '1', overflowY: 'auto' }}
        cardHeight='250px'
        cardWidth={`${cardWidth}px`}
        totalPages={Math.ceil(docsCount / DOCS_PER_PAGE)}
        currentPage={currentPage}
        onLoadPage={(page) => { setCurrentPage(page); }}
        cardRenderer={(data) => {
          let description = '';
          if (data.coloquial && data.coloquial.length > 0) {
            description = data.coloquial;
          } else {
            description = data.displayName;
          }
          return (
            <div key={data._id} className={css(styles.gridCard)}>
              <div className={css(styles.cardHeader)}>
                <div>
                  <span>{`Code: ${data.code} - `}</span>
                  {/* <span>{data.displayName}</span> */}
                  {isFiltered
                    ? getHighlightedText(description, search)
                    : <span>{description}</span>
                  }
                </div>
                <div className='ml-4 mr-2 fx fx__v-center'>
                  <i className='fa fa-ellipsis-h' />
                </div>
              </div>
              <div className={css(styles.cardDataContainer)}>
                {/* Bullet points data */}
                <div className='pl-2 pb-2'>
                  <div>
                    <i className={`fa fa-circle pr-2 ${css(styles.bulletItem)}`} />
                    {`Grouping: ${data.grouping}`}
                  </div>
                  <div>
                    <i className={`fa fa-circle pr-2 ${css(styles.bulletItem)}`} />
                    <span>Classification:&nbsp;</span>
                    {isFiltered
                      ? getHighlightedText(data.classification, search)
                      : <span>{data.classification}</span>
                    }
                  </div>
                  <div>
                    <i className={`fa fa-circle pr-2 ${css(styles.bulletItem)}`} />
                    <span>Specialization:&nbsp;</span>
                    {isFiltered
                      ? getHighlightedText(data.specialization, search)
                      : <span>{data.specialization}</span>
                    }
                  </div>
                </div>
                <div className='line-clamp'>
                  <span>Definition:&nbsp;</span>
                  {isFiltered
                    ? getHighlightedText(data.definition, search)
                    : <span>{data.definition}</span>
                  }
                </div>
              </div>
            </div>
          );
        }}
      />
    </div>
  );
};

export default TaxonomyDataGrid;
